import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createArcPath, createSVGElement } from "../../utils/svgHelpers";
import {
  incrementDartCount,
  updateResult,
  addHit,
  completeRound,
} from "../../store/slices/dartSlice";
import { clearStatistics } from "../../store/slices/dartSlice";
import "../../styles/Dartboard.css";

const SECTOR_VALUES = [
  20, 1, 18, 4, 13, 6, 10, 15, 2, 17, 3, 19, 7, 16, 8, 11, 14, 9, 12, 5,
];

const Dartboard = ({ isZoomed }) => {
  const dispatch = useDispatch();

  const svgRef = useRef(null);
  const [clickDisabled, setClickDisabled] = useState(false);

  const currentRound = useSelector((state) => state.dartGame.currentRound);
  const isDisabled = useSelector((state) => state.dartGame.isDisabled);

  const handleSegmentClick = (e) => {
    if (clickDisabled) return;
    const svg = svgRef.current;
    if (!svg) return;

    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const svgP = pt.matrixTransform(svg.getScreenCTM().inverse());

    const target = e.target;
    dispatch(clearStatistics());
    if (target.classList && target.classList.contains("dart-segment")) {
      setClickDisabled(true);
      // Alte Highlights entfernen
      const highlighted = svg.querySelectorAll(".dart-segment.highlight");
      highlighted.forEach((el) => el.classList.remove("highlight"));
      target.classList.add("highlight");
      setTimeout(() => target.classList.remove("highlight"), 1000);

      const score = target.dataset.score;
      const label = target.dataset.label;

      dispatch(incrementDartCount());
      dispatch(updateResult(`Getroffen: ${label} – Punkte: ${score}`));
      dispatch(addHit({ label, score, x: svgP.x, y: svgP.y }));
    } else {
      dispatch(updateResult("Kein Treffer – außerhalb der Scheibe!"));
      dispatch(incrementDartCount());
      dispatch(addHit({ label: "Out", score: 0, x: svgP.x, y: svgP.y }));
    }
    if (currentRound.length + 1 === 3) {
      setClickDisabled(true);
    }

    // Falls es der dritte Dart im aktuellen Wurf war:
    if (currentRound.length + 1 === 3) {
      setTimeout(() => {
        dispatch(completeRound());
        setClickDisabled(false);
      }, 1000);
    } else {
      setClickDisabled(false);
    }
  };

  useEffect(() => {
    const drawDartboard = () => {
      const svg = svgRef.current;
      if (!svg) return;

      // Vorhandenen SVG-Inhalt löschen
      while (svg.firstChild) {
        svg.removeChild(svg.firstChild);
      }

      // --- SVG-Defs (Filter, Muster etc.) ---
      const defs = createSVGElement("defs");

      // Drop Shadow Filter
      const dropShadow = createSVGElement("filter");
      dropShadow.setAttribute("id", "dropShadow");
      const feGaussianBlur = createSVGElement("feGaussianBlur");
      feGaussianBlur.setAttribute("in", "SourceAlpha");

      feGaussianBlur.setAttribute("stdDeviation", "2");
      dropShadow.appendChild(feGaussianBlur);

      const feOffset = createSVGElement("feOffset");
      feOffset.setAttribute("dx", "1");
      feOffset.setAttribute("dy", "1");
      dropShadow.appendChild(feOffset);

      const feMerge = createSVGElement("feMerge");
      const feMergeNode1 = createSVGElement("feMergeNode");
      feMerge.appendChild(feMergeNode1);
      const feMergeNode2 = createSVGElement("feMergeNode");
      feMergeNode2.setAttribute("in", "SourceGraphic");
      feMerge.appendChild(feMergeNode2);
      dropShadow.appendChild(feMerge);

      defs.appendChild(dropShadow);
      svg.appendChild(defs);

      // Outer Bull
      const outerBull = createSVGElement("circle");
      outerBull.setAttribute("cx", "0");
      outerBull.setAttribute("cy", "0");
      outerBull.setAttribute("r", "30");
      outerBull.setAttribute("class", "dart-segment outer-bull");
      outerBull.dataset.score = "25";
      outerBull.dataset.label = "S25";
      svg.appendChild(outerBull);

      // Bullseye
      const bullseye = createSVGElement("circle");
      bullseye.setAttribute("cx", "0");
      bullseye.setAttribute("cy", "0");
      bullseye.setAttribute("r", "15");
      bullseye.setAttribute("class", "dart-segment bullseye");
      bullseye.dataset.score = "50";
      bullseye.dataset.label = "D25";
      svg.appendChild(bullseye);

      // Ring-Definitionen
      const ringDefs = [
        { label: "single-inner", innerR: 30, outerR: 100, multiplier: 1 },
        { label: "triple-ring", innerR: 98, outerR: 110, multiplier: 3 },
        { label: "single-outer", innerR: 110, outerR: 180, multiplier: 1 },
        { label: "double-ring", innerR: 178, outerR: 190, multiplier: 2 },
      ];

      // 20 Sektoren
      for (let i = 0; i < 20; i++) {
        const baseValue = SECTOR_VALUES[i];
        const sectorAngle = 18;
        const startAngle = i * sectorAngle - 90 - sectorAngle / 2;
        const endAngle = startAngle + sectorAngle;

        ringDefs.forEach((def) => {
          const path = createSVGElement("path");
          const pathD = createArcPath(
            0,
            0,
            def.innerR,
            def.outerR,
            startAngle,
            endAngle
          );
          path.setAttribute("d", pathD);
          let cssClass = `dart-segment ${def.label}-${i % 2}`;
          path.setAttribute("class", cssClass);
          path.setAttribute("filter", "url(#dropShadow)");

          // Punktzahl & Label
          const finalScore = baseValue * def.multiplier;
          path.dataset.score = finalScore;
          const customLabel =
            def.multiplier === 1
              ? def.label === "single-inner"
                ? `LS${baseValue}` // "little single"
                : `S${baseValue}`
              : def.multiplier === 3
              ? `T${baseValue}`
              : `D${baseValue}`;
          path.dataset.label = customLabel;

          svg.appendChild(path);
        });
      }

      // Sektornummern
      for (let i = 0; i < 20; i++) {
        const numberValue = SECTOR_VALUES[i];
        const angleDeg = i * 18 - 90;
        const angleRad = (Math.PI / 180) * angleDeg;
        const r = 215;
        const x = r * Math.cos(angleRad);
        const y = r * Math.sin(angleRad);

        const textEl = createSVGElement("text");
        textEl.setAttribute("x", x.toFixed(2));
        textEl.setAttribute("y", y.toFixed(2));
        textEl.setAttribute("class", "dart-number-outer");
        textEl.textContent = numberValue;
        svg.appendChild(textEl);
      }
    };

    drawDartboard();
  }, []);

  // Zoom je nach Zustand
  const viewBox = isZoomed ? "-125 -250 250 250" : "-250 -250 500 500";

  return (
    <div className="p-2">
      <svg
        ref={svgRef}
        id={isZoomed ? "dartboard-zoomed" : "dartboard"}
        viewBox={viewBox}
        onClick={handleSegmentClick}
        style={{
          pointerEvents: isDisabled ? "none" : "auto",
          opacity: isDisabled ? 0.8 : 1,
        }}
      ></svg>
    </div>
  );
};
export default Dartboard;
